// NotificationBell — iter66 dropdown rebuild.
//
// Tapping the bell opens a small dropdown (instead of navigating) with the
// 5 most recent notifications. Each row has a small ti-x dismiss that calls
// DELETE /api/notifications/{id} and removes it from the dropdown.
// Outside-tap closes the dropdown. "See all" link → /notifications.
// Empty state shows "You're all caught up" / "Todo al día".
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, X, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";

const POLL_MS = 30000;

export function NotificationBell({ labeled = false, testidPrefix = "header" }) {
  const { t, lang } = useI18n();
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/notifications", { params: { limit: 5 } });
        if (cancelled) return;
        setUnread(data.unread || 0);
        setItems(Array.isArray(data.items) ? data.items.slice(0, 5) : []);
      } catch { /* ignore */ }
    };
    tick();
    const i = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(i); };
  }, []);

  // Outside tap closes
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  // iter66 — Reload list when dropdown opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/notifications", { params: { limit: 5 } });
        if (cancelled) return;
        setUnread(data.unread || 0);
        setItems(Array.isArray(data.items) ? data.items.slice(0, 5) : []);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const dismiss = async (nid) => {
    setItems((prev) => prev.filter((n) => n.id !== nid));
    setUnread((u) => Math.max(0, u - 1));
    try { await api.delete(`/notifications/${nid}`); } catch { /* ignore */ }
  };

  return (
    <div ref={wrapRef} className="relative">
      {labeled ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={lang === "es" ? "Alertas" : "Alerts"}
          data-testid="notification-bell"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            background: "transparent",
            border: "none",
            padding: 0,
            minHeight: 48,
            justifyContent: "flex-end",
          }}
        >
          <span
            className="relative grid place-items-center rounded-full"
            style={{
              width: 34,
              height: 34,
              background: "rgba(232,184,75,0.12)",
              border: "1px solid rgba(232,184,75,0.35)",
              color: "#E8B84B",
            }}
          >
            <Bell style={{ width: 15, height: 15 }} strokeWidth={1.6} />
            {unread > 0 && (
              <span
                data-testid="notification-bubble"
                className="absolute grid place-items-center"
                style={{
                  top: -3,
                  right: -3,
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  background: "#E8B84B",
                  border: "2px solid #0B0B0B",
                  color: "#0B0B0B",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 8,
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                {unread >= 9 ? "9+" : unread}
              </span>
            )}
          </span>
          <span
            data-testid={`${testidPrefix}-alerts-label`}
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 9,
              fontWeight: 500,
              color: "#E8B84B",
              letterSpacing: "0.3px",
              textAlign: "center",
              marginTop: 3,
              whiteSpace: "nowrap",
            }}
          >
            {lang === "es" ? "Alertas" : "Alerts"}
          </span>
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="dialog"
          aria-expanded={open}
          data-testid="notification-bell"
          className="relative w-9 h-9 grid place-items-center rounded-full border border-gold-soft text-foreground hover:text-gold transition-colors"
          style={{ minHeight: 44, minWidth: 44 }}
        >
          <Bell className="w-4 h-4" strokeWidth={1.5} />
          {unread > 0 && (
            <span
              data-testid="notification-bubble"
              className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-gold text-[10px] font-medium tracking-wider grid place-items-center"
              style={{ color: "#0B0B0B", fontFamily: "var(--font-sans, 'DM Sans')" }}
            >
              {unread >= 9 ? "9+" : unread}
            </span>
          )}
        </button>
      )}

      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 mt-2 z-50 w-[320px] max-w-[92vw]"
          style={{
            background: "#171717",
            border: "1px solid rgba(232,184,75,0.3)",
            borderRadius: 14,
            boxShadow: "0 24px 60px rgba(0,0,0,0.55)",
            overflow: "hidden",
          }}
          role="dialog"
        >
          <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: "0.5px solid #222" }}>
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#E8B84B", letterSpacing: 1.5, textTransform: "uppercase" }}>
              {lang === "es" ? "Notificaciones" : "Notifications"}
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              data-testid="bell-dropdown-close"
              className="grid place-items-center rounded-full hover:bg-white/[0.05]"
              style={{ width: 32, height: 32, color: "#B0AAA3" }}
            >
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>

          {/* Items */}
          <div className="max-h-[360px] overflow-y-auto" data-testid="bell-dropdown-list">
            {items.length === 0 ? (
              <div
                data-testid="bell-dropdown-empty"
                className="px-4 py-6 text-center"
                style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#B0AAA3" }}
              >
                {t("bell_empty")}
              </div>
            ) : (
              items.map((n) => (
                <BellRow
                  key={n.id}
                  n={n}
                  lang={lang}
                  onDismiss={() => dismiss(n.id)}
                  onNavigate={() => setOpen(false)}
                  dismissLabel={t("bell_dismiss")}
                />
              ))
            )}
          </div>

          {/* See all */}
          <Link
            to="/notifications"
            onClick={() => setOpen(false)}
            data-testid="bell-see-all"
            className="flex items-center justify-center gap-1 px-4 py-3 transition-colors hover:bg-[rgba(232,184,75,0.05)]"
            style={{
              borderTop: "0.5px solid #222",
              color: "#E8B84B",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 13,
              fontWeight: 500,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            {t("bell_see_all")}
            <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
      )}
    </div>
  );
}

function BellRow({ n, lang, onDismiss, onNavigate, dismissLabel }) {
  const rawTitle = n.title;
  const rawBody = n.body;
  const title = typeof rawTitle === "string" ? rawTitle : (rawTitle?.[lang] || rawTitle?.en || "");
  const body = typeof rawBody === "string" ? rawBody : (rawBody?.[lang] || rawBody?.en || "");
  const Wrapper = n.link ? Link : "div";
  const wrapperProps = n.link
    ? { to: n.link, onClick: onNavigate, "data-testid": `bell-row-link-${n.id}` }
    : { "data-testid": `bell-row-${n.id}` };
  return (
    <div className="relative" data-testid={`bell-row-${n.id}`}>
      <Wrapper
        {...wrapperProps}
        className="block px-4 py-3 transition-colors hover:bg-[rgba(255,255,255,0.03)]"
        style={{ borderBottom: "0.5px solid #1c1c1c" }}
      >
        <div
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 14,
            fontWeight: 500,
            color: "#F5F1E8",
            lineHeight: 1.3,
            paddingRight: 32,
          }}
          className="truncate"
        >
          {title}
        </div>
        {body && (
          <div
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 12,
              color: "#B0AAA3",
              lineHeight: 1.4,
              marginTop: 2,
              paddingRight: 32,
            }}
            className="line-clamp-2"
          >
            {body}
          </div>
        )}
      </Wrapper>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDismiss(); }}
        aria-label={dismissLabel}
        data-testid={`bell-row-dismiss-${n.id}`}
        className="absolute top-2 right-2 grid place-items-center rounded-full hover:bg-white/[0.06]"
        style={{ width: 28, height: 28, color: "#B0AAA3" }}
      >
        <X className="w-3.5 h-3.5" strokeWidth={1.5} />
      </button>
    </div>
  );
}
